import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { ServerDomainInfo } from '@/api/type-server'
import { Button, Input, message, Modal, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown.tsx'
import { Network } from '@/models/network'
interface ClusterCreateOrUpdateModalProps {
  network?: Network
  domainInfos: ServerDomainInfo[]
  onSuccess: () => void
  onClose: () => void
}

export function ClusterCreateOrUpdateModal({
  network,
  domainInfos,
  onSuccess = () => {},
  onClose = () => {},
}: ClusterCreateOrUpdateModalProps) {
  const [loading, setLoading] = useState(false)
  const [meshType, setMeshType] = useState<number>(0)
  const [selectedDomain, setSelectedDomain] = useState<number>(0)
  const [name, setName] = useState(network?.name ?? '')
  const { t } = useTranslation()

  const meshTypeOptions = [
    { label: t('mesh.type.networkGroup'), value: 0 },
    { label: t('mesh.type.loadBalancer'), value: 1, disabled: true },
    { label: t('mesh.type.cluster'), value: 2, disabled: true },
  ]

  const onOK = () => {
    setLoading(true)
    ;(network
      ? API.updateMesh({
          res_id: network?.resID ?? network,
          name: name,
        })
      : API.createMesh({
          name: name,
          cluster_type: meshType,
          domain: selectedDomain,
        })
    )
      .then((res) => {
        if (res.code === 0) {
          message.success(t('common.message.success'))
          onSuccess()
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  return (
    <Modal
      className="w-[640px]"
      open={true}
      onClose={() => {
        if (!loading) {
          onClose()
        }
      }}
    >
      <Modal.Header>
        {network ? (
          <Trans i18nKey="mesh.clusterCreateOrUpdateModal.editTitle">Edit</Trans>
        ) : (
          <Trans i18nKey="mesh.clusterCreateOrUpdateModal.createTitle">Create New</Trans>
        )}
      </Modal.Header>
      <Modal.Body>
        <div className="flex-row ">
          {network ? null : (
            <>
              <div className="w-full t-lg text-primary">
                <div className="mb-2 text-with-star">
                  <Trans i18nKey="mesh.clusterCreateOrUpdateModal.form.meshType">Mesh Type</Trans>
                </div>
                <div className="w-full t-lg text-primary mb-2">
                  <Dropdown
                    options={meshTypeOptions}
                    value={meshType}
                    disabled={false}
                    onChange={(value) => setMeshType(value as number)}
                  />
                </div>
              </div>
              <div className="w-full t-lg text-primary">
                <div className="mb-2 text-with-star">
                  <Trans i18nKey="mesh.clusterCreateOrUpdateModal.form.region">Region</Trans>
                </div>
                <div className="w-full t-lg text-primary mb-2">
                  <Dropdown
                    options={domainInfos.map((info) => ({
                      label: info.displayName,
                      value: info.domain,
                      className: 't-sm',
                    }))}
                    value={selectedDomain}
                    disabled={false}
                    onChange={(value) => setSelectedDomain(Number(value))}
                  />
                </div>
              </div>
            </>
          )}
          <div className="w-full t-lg text-primary">
            <div className="mb-2 text-with-star">
              <Trans i18nKey="mesh.clusterCreateOrUpdateModal.form.name">Name</Trans>
            </div>
            <div className="w-full t-lg text-primary mb-2">
              <Input
                placeholder={t('mesh.clusterCreateOrUpdateModal.form.namePlaceholder')}
                value={name}
                disabled={loading}
                maxLength={30}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="outline"
          onClick={() => {
            if (!loading) {
              onClose()
            }
          }}
        >
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          disabled={loading || name?.length <= 0 || name === network?.name}
          onClick={onOK}
        >
          {network ? (
            <Trans i18nKey="mesh.clusterCreateOrUpdateModal.form.confirmBtn">Confirm</Trans>
          ) : (
            <Trans i18nKey="mesh.clusterCreateOrUpdateModal.form.createBtn">Create</Trans>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
